import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import BasicSnackbar from '../shared/BasicSnackbar';
import { useTranslation } from 'react-i18next';
import useCompanySettings from 'api-new/useCompanySettings';
import vlqrApi from 'utils/vlqrApi';

const DataRetentionSettings = ({ companyId }) => {
    const { t } = useTranslation();
    const { settings, isLoading, updateSettings } = useCompanySettings(companyId);
    const [notification, setNotification] = useState(null);
    const [preview, setPreview] = useState(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [isRunningNow, setIsRunningNow] = useState(false);

    const validationSchema = useMemo(() => Yup.object().shape({
        dataRetentionMonths: Yup.number()
            .integer()
            .min(1, t('userAccountSettingsPage.dataRetention.errors.mustBeOneOrMore'))
            .required()
    }), [t]);

    const handleFormSubmission = async (formData, form) => {
        try {
            await updateSettings({ dataRetentionMonths: formData.dataRetentionMonths });
            setNotification({ key: 'Success', message: t('userAccountSettingsPage.dataRetention.text.savedSuccessfully') });
        } catch {
            setNotification({ key: 'Error', message: t('userAccountSettingsPage.dataRetention.errors.saveFailed') });
        } finally {
            form.setSubmitting(false);
        }
    };

    const handleRunNow = async () => {
        setIsLoadingPreview(true);
        try {
            const response = await vlqrApi.get(`DataRetention/companies/${companyId}/preview`);
            setPreview(response.data);
        } catch {
            setNotification({ key: 'RunNowError', message: t('userAccountSettingsPage.dataRetention.errors.runNowFailed') });
        } finally {
            setIsLoadingPreview(false);
        }
    };

    const handleConfirmRunNow = async () => {
        setPreview(null);
        setIsRunningNow(true);
        try {
            await vlqrApi.post(`DataRetention/companies/${companyId}/runNow`);
            setNotification({ key: 'RunNowSuccess', message: t('userAccountSettingsPage.dataRetention.text.runNowSuccessfully') });
        } catch {
            setNotification({ key: 'RunNowError', message: t('userAccountSettingsPage.dataRetention.errors.runNowFailed') });
        } finally {
            setIsRunningNow(false);
        }
    };

    if (isLoading || !settings) {
        return null;
    }

    return (
        <div>
            <Formik
                enableReinitialize
                initialValues={{ dataRetentionMonths: settings.dataRetentionMonths }}
                validationSchema={validationSchema}
                onSubmit={handleFormSubmission}>
                {({ values, errors, touched, dirty, isSubmitting, handleChange, handleSubmit, handleReset }) => (
                    <form autoComplete="off">
                        <TextField
                            fullWidth
                            variant="standard"
                            margin="normal"
                            type="number"
                            id="dataRetentionMonths"
                            label={t('userAccountSettingsPage.dataRetention.labels.retentionMonths')}
                            value={values.dataRetentionMonths}
                            onChange={handleChange}
                            error={!!errors.dataRetentionMonths && touched.dataRetentionMonths}
                            helperText={errors.dataRetentionMonths && touched.dataRetentionMonths ? errors.dataRetentionMonths : undefined}
                            slotProps={{ htmlInput: { min: 1 } }}
                        />
                        <br /><br />
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting || !dirty}>
                            {t('userAccountSettingsPage.dataRetention.buttons.save')}
                        </Button>
                        <Fade in={dirty}>
                            <Button onClick={handleReset}>{t('common.buttons.resetForm')}</Button>
                        </Fade>

                        {settings.dataRetentionMonths >= 1 && (
                            <div style={{ marginTop: 24, marginBottom: 24 }}>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    {t('userAccountSettingsPage.dataRetention.text.runNowHint')}
                                </Typography>
                                <Button
                                    color="warning"
                                    variant="outlined"
                                    onClick={handleRunNow}
                                    disabled={isLoadingPreview || isRunningNow}>
                                    {t('userAccountSettingsPage.dataRetention.buttons.runNow')}
                                </Button>
                            </div>
                        )}
                    </form>
                )}
            </Formik>

            <Dialog open={preview !== null} onClose={() => setPreview(null)}>
                <DialogTitle>{t('userAccountSettingsPage.dataRetention.text.runNowConfirmTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('userAccountSettingsPage.dataRetention.text.runNowConfirmMessage', {
                            activityCount: preview?.activityCount,
                            projectCount: preview?.projectCount
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreview(null)}>{t('common.buttons.cancel')}</Button>
                    <Button color="warning" variant="contained" onClick={handleConfirmRunNow}>
                        {t('common.buttons.delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            <BasicSnackbar
                notification={notification}
                onClose={() => setNotification(null)}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
                {t('userAccountSettingsPage.dataRetention.text.removeCompanyDataHeading')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
                {t('userAccountSettingsPage.dataRetention.text.removeCompanyDataHint')}{' '}
                <Link href="mailto:support@visionslive.com">support@visionslive.com</Link>
            </Typography>
        </div>
    );
};

DataRetentionSettings.propTypes = {
    companyId: PropTypes.string.isRequired
};

export default DataRetentionSettings;
